<template>
  <div>
    <b-modal
      id="staffNotificationModal"
      v-model="modalVisible"
      :title="currentNotification ? currentNotification.name : ''"
      :no-close-on-backdrop="true"
      :no-close-on-esc="true"
      :hide-header-close="true"
      modal-class="staff-notification-modal"
      size="lg"
      centered
      @hide="onModalHide"
    >
      <template v-if="currentNotification">
        <div class="mb-3">
          <span v-if="isMandatory" class="badge badge-danger mr-2">Zorunlu</span>
          <span v-else class="badge badge-info mr-2">Bilgilendirme</span>
          <strong>{{ currentNotification.subject }}</strong>
        </div>

        <div class="staff-notification-detail mb-3" v-safe-html="currentNotification.detail" />

        <div v-if="legacyPdfUrl" class="mb-3">
          <a href="#" class="btn btn-outline-secondary btn-sm" @click.prevent="handleLegacyPdfDownload">
            PDF'i Görüntüle
          </a>
        </div>

        <div v-if="attachmentList.length > 0" class="staff-notification-attachments mb-3">
          <p class="mb-2 font-weight-bold small text-muted">Ek Dosyalar</p>
          <ul class="row list-unstyled mb-0">
            <li v-for="attachment in attachmentList" :key="attachmentKey(attachment)" class="mb-2 col-auto">
              <a href="#" class="btn btn-outline-secondary btn-sm" @click.prevent="handleAttachmentOpen(attachment)">
                <i class="fas fa-external-link-alt mr-1" />
                {{ attachment.fileName || "Dosya" }}
              </a>
            </li>
          </ul>
        </div>

        <div v-if="isMandatory" class="mt-3">
          <b-form-checkbox v-model="readConfirmed">Okudum ve anladım</b-form-checkbox>
        </div>

        <div class="mt-2 text-muted small">{{ currentIndex + 1 }} / {{ notifications.length }}</div>
      </template>

      <template #modal-footer>
        <div class="d-flex justify-content-end w-100">
          <b-button v-if="isMandatory" key="b-button-1" variant="primary" :disabled="!readConfirmed || loadingConfirm" @click="handleConfirm">
            <span v-if="loadingConfirm">Onaylanıyor...</span>
            <span v-else>Onay</span>
          </b-button>
          <b-button v-else key="b-button-2" variant="secondary" :disabled="loadingClose" @click="handleClose">
            <span v-if="loadingClose">Kapatılıyor...</span>
            <span v-else>Kapat</span>
          </b-button>
        </div>
      </template>
    </b-modal>
    <loading :active.sync="loadingFullPage" :is-full-page="true" />
  </div>
</template>

<script>
import Loading from "vue-loading-overlay";
const ATTACHMENT_MIME_BY_EXTENSION = {
  pdf: "application/pdf",
  png: "image/png",
  jpg: "image/jpeg",
  jpeg: "image/jpeg",
  bmp: "image/bmp",
  doc: "application/msword",
  docx: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
};

export default {
  name: "StaffNotificationModal",
  components: {
    Loading,
  },
  props: {
    notifications: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      modalVisible: false,
      currentIndex: 0,
      readConfirmed: false,
      loadingConfirm: false,
      loadingClose: false,
      loadingLogStaffNotificationDownload: false,
    };
  },
  computed: {
    currentNotification() {
      if (!this.sortedNotifications || this.currentIndex >= this.sortedNotifications.length) return null;
      return this.sortedNotifications[this.currentIndex];
    },
    sortedNotifications() {
      if (!this.notifications || this.notifications.length === 0) return [];
      return [...this.notifications].sort((a, b) => {
        if (a.displayType === "MANDATORY" && b.displayType !== "MANDATORY") return -1;
        if (a.displayType !== "MANDATORY" && b.displayType === "MANDATORY") return 1;
        return 0;
      });
    },
    isMandatory() {
      return this.currentNotification && this.currentNotification.displayType === "MANDATORY";
    },
    legacyPdfUrl() {
      if (!this.currentNotification || !this.currentNotification.pdfUrl) return null;
      const hasAttachments = this.currentNotification.attachments && this.currentNotification.attachments.length > 0;
      return hasAttachments ? null : this.currentNotification.pdfUrl;
    },
    attachmentList() {
      if (!this.currentNotification || !this.currentNotification.attachments) return [];
      return this.currentNotification.attachments;
    },
    loadingFullPage() {
      return this.loadingLogStaffNotificationDownload;
    },
  },
  watch: {
    notifications(val) {
      if (!val || val.length === 0) return;
      if (this.modalVisible) return;
      this.currentIndex = 0;
      this.readConfirmed = false;
      this.modalVisible = true;
      this.markCurrentShown();
    },
    currentIndex() {
      this.readConfirmed = false;
      this.markCurrentShown();
    },
  },
  methods: {
    attachmentKey(attachment) {
      return attachment.id || attachment.fileUrl;
    },
    markCurrentShown() {
      const notif = this.currentNotification;
      if (!notif) return;
      if (notif.displayType === "INFORMATIONAL") {
        this.$store.commit("addShownStaffNotificationId", notif.id);
      }
      this.$store.dispatch("markStaffNotificationShown", notif.id).catch(() => {});
      if (notif.displayType === "INFORMATIONAL" && notif.showMode === "ONCE") {
        // shown backend kaydı yukarıda tüm tipler için atılıyor
      }
    },
    handleConfirm() {
      if (!this.currentNotification) return;
      this.loadingConfirm = true;
      this.$store
        .dispatch("confirmStaffNotification", this.currentNotification.id)
        .then(() => {
          this.loadingConfirm = false;
          this.goToNext();
        })
        .catch(() => {
          this.loadingConfirm = false;
        });
    },
    handleClose() {
      if (!this.currentNotification) return;
      this.loadingClose = true;
      this.$store
        .dispatch("closeStaffNotification", this.currentNotification.id)
        .then(() => {
          this.loadingClose = false;
          this.goToNext();
        })
        .catch(() => {
          this.loadingClose = false;
          this.goToNext();
        });
    },
    handleLegacyPdfDownload() {
      if (!this.legacyPdfUrl) return;
      this.openAttachmentPreview({ fileUrl: this.legacyPdfUrl, fileName: "bilgilendirme.pdf", fileType: "application/pdf" });
    },
    handleAttachmentOpen(attachment) {
      if (!this.currentNotification || !attachment || !attachment.fileUrl) return;
      const attachmentId = attachment.id || null;
      this.loadingLogStaffNotificationDownload = true;
      this.$store
        .dispatch("logStaffNotificationDownload", {
          informationId: this.currentNotification.id,
          attachmentId,
        })
        .catch(() => {
          this.callAlert(4, "İşlem gerçekleştirilememektedir.", "danger", "İşlem Hatası");
        })
        .finally(() => {
          this.loadingLogStaffNotificationDownload=false;
          this.openAttachmentPreview(attachment);
        });
    },
    resolveAttachmentMimeType(attachment) {
      if (attachment.fileType) return attachment.fileType;
      const fileName = attachment.fileName || attachment.fileUrl || "";
      const extension = fileName.split(".").pop().toLowerCase();
      return ATTACHMENT_MIME_BY_EXTENSION[extension] || "application/octet-stream";
    },
    getAttachmentCategory(mimeType, fileName) {
      if (mimeType.startsWith("image/")) return "image";
      if (mimeType === "application/pdf") return "pdf";
      const extension = (fileName || "").split(".").pop().toLowerCase();
      if (["doc", "docx"].includes(extension) || mimeType.includes("word") || mimeType === "application/msword") {
        return "word";
      }
      if (mimeType === "application/pdf" || extension === "pdf") return "pdf";
      return "other";
    },
    openBlobInNewTab(blob, mimeType, fileName) {
      const typedBlob = new Blob([blob], { type: mimeType || blob.type || "application/octet-stream" });
      const blobUrl = URL.createObjectURL(typedBlob);
      const category = this.getAttachmentCategory(mimeType, fileName);

      if (category === "word") {
        const link = document.createElement("a");
        link.href = blobUrl;
        link.download = fileName;
        link.click();
        setTimeout(() => URL.revokeObjectURL(blobUrl), 120000);
        return;
      }

      const newTab = window.open(blobUrl, "_blank");
      if (!newTab) {
        const link = document.createElement("a");
        link.href = blobUrl;
        link.target = "_blank";
        link.rel = "noopener";
        link.click();
      }
      setTimeout(() => URL.revokeObjectURL(blobUrl), 120000);
    },
    async openAttachmentPreview(attachment) {
      const fileName = attachment.fileName || "dosya";
      const mimeType = this.resolveAttachmentMimeType(attachment);

      try {
        const blob = await this.$store.dispatch("downloadOtherFile", { fileUrl: attachment.fileUrl });
        this.openBlobInNewTab(blob, mimeType, fileName);
      } catch (error) {
        this.$store.commit("setAlertText", "Dosya açılırken bir hata oluştu.");
        this.$store.commit("setAlertTitle", "İşlem Hatası");
        this.$store.commit("setAlertVariant", "danger");
        this.$store.commit("setDismissCountDown", 4);
      }
    },
    goToNext() {
      if (this.currentIndex + 1 < this.sortedNotifications.length) {
        this.currentIndex++;
      } else {
        this.modalVisible = false;
        this.$emit("allMandatoryConfirmed");
      }
    },
    onModalHide(evt) {
      if (this.isMandatory) {
        evt.preventDefault();
      }
    },
  },
};
</script>

<style scoped>
.staff-notification-detail {
  max-height: 300px;
  overflow-y: auto;
  border: 1px solid #dee2e6;
  border-radius: 4px;
  padding: 12px;
  background: #f8f9fa;
}
</style>

<style>
.staff-notification-modal ~ .modal-backdrop {
  opacity: 0.75 !important;
}
</style>
